import type { TicketStatus } from "@zplanem/responses"
import type React from "react"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { TicketStatusBadge } from "./ticket-card"

const ChevronIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.75"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="h-3 w-3"
		aria-hidden="true"
	>
		<path d="m4 6 4 4 4-4" />
	</svg>
)

const CheckIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="h-3.5 w-3.5"
		aria-hidden="true"
	>
		<path d="m3 8 3.5 3.5L13 5" />
	</svg>
)

export function TicketStatusMenu({
	status,
	statuses,
	pending,
	onSelect,
}: {
	status: TicketStatus
	statuses: readonly TicketStatus[]
	pending: boolean
	onSelect: (status: TicketStatus) => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const [open, setOpen] = useState(false)
	const [activeIndex, setActiveIndex] = useState(0)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const optionRefs = useRef<Array<HTMLButtonElement | null>>([])
	const selectedIndex = Math.max(statuses.indexOf(status), 0)

	function closeMenu(restoreFocus: boolean) {
		setOpen(false)
		if (restoreFocus) {
			requestAnimationFrame(() => triggerRef.current?.focus())
		}
	}

	function openMenu() {
		setActiveIndex(selectedIndex)
		setOpen(true)
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: closeMenu closes only over the stable setOpen + triggerRef; listing it would re-subscribe the document listeners on every render
	useEffect(() => {
		if (!open) return
		const handleDown = (e: MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(e.target as Node)
			) {
				closeMenu(false)
			}
		}
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") closeMenu(true)
		}
		window.addEventListener("mousedown", handleDown)
		window.addEventListener("keydown", handleKey)
		return () => {
			window.removeEventListener("mousedown", handleDown)
			window.removeEventListener("keydown", handleKey)
		}
	}, [open])

	// Layout effect, not effect + rAF: focus must sit on the active option in
	// the same task that renders its tabindex="0", or an ArrowDown pressed
	// right after opening lands on the trigger (which ignores arrows while
	// open) and the keystroke is silently lost.
	useLayoutEffect(() => {
		if (!open) return
		optionRefs.current[activeIndex]?.focus()
	}, [open, activeIndex])

	function selectStatus(next: TicketStatus) {
		closeMenu(true)
		if (next !== status) onSelect(next)
	}

	function focusOption(index: number) {
		// The layout effect above moves focus once the new activeIndex renders.
		setActiveIndex(index)
	}

	function onOptionKeyDown(
		event: React.KeyboardEvent<HTMLButtonElement>,
		index: number,
		next: TicketStatus,
	) {
		if (event.key === "ArrowDown") {
			event.preventDefault()
			focusOption((index + 1) % statuses.length)
			return
		}
		if (event.key === "ArrowUp") {
			event.preventDefault()
			focusOption((index - 1 + statuses.length) % statuses.length)
			return
		}
		if (event.key === "Home") {
			event.preventDefault()
			focusOption(0)
			return
		}
		if (event.key === "End") {
			event.preventDefault()
			focusOption(statuses.length - 1)
			return
		}
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault()
			selectStatus(next)
			return
		}
		if (event.key === "Escape") {
			event.preventDefault()
			closeMenu(true)
		}
	}

	return (
		<div ref={wrapperRef} className="relative">
			<button
				ref={triggerRef}
				type="button"
				onClick={() => {
					if (open) {
						closeMenu(false)
					} else {
						openMenu()
					}
				}}
				onKeyDown={(event) => {
					if (
						!open &&
						(event.key === "ArrowDown" ||
							event.key === "ArrowUp" ||
							event.key === "Enter" ||
							event.key === " ")
					) {
						event.preventDefault()
						openMenu()
					}
				}}
				disabled={pending}
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-label={`${t.tickets.statusLabel}: ${t.tickets.status[status]}`}
				className="inline-flex items-center gap-1 rounded-sm transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50"
			>
				<TicketStatusBadge status={status} t={t.tickets} />
				<ChevronIcon />
			</button>
			{open ? (
				<div
					role="listbox"
					aria-label={t.tickets.statusLabel}
					className="absolute left-0 z-50 mt-2 w-48 rounded-xl border border-border bg-popover py-1 text-popover-foreground shadow-xl"
				>
					{statuses.map((next, index) => (
						<button
							key={next}
							ref={(element) => {
								optionRefs.current[index] = element
							}}
							type="button"
							role="option"
							aria-selected={next === status}
							tabIndex={index === activeIndex ? 0 : -1}
							onClick={() => selectStatus(next)}
							onKeyDown={(event) => onOptionKeyDown(event, index, next)}
							onMouseEnter={() => setActiveIndex(index)}
							className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-foreground/80 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:bg-accent focus-visible:text-foreground"
						>
							<span>{t.tickets.status[next]}</span>
							{next === status ? (
								<span className="text-[color:var(--ring)]">
									<CheckIcon />
								</span>
							) : null}
						</button>
					))}
				</div>
			) : null}
		</div>
	)
}
